const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

let _refreshInFlight = null;

async function tryRefresh() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    const refresh = localStorage.getItem("refresh");
    if (!refresh) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.access) localStorage.setItem("access", data.access);
      if (data.refresh) localStorage.setItem("refresh", data.refresh);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => { _refreshInFlight = null; }, 0);
    }
  })();
  return _refreshInFlight;
}

function redirectToLogin() {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
    window.location.href = "/login";
  }
}

async function request(endpoint, options = {}) {
  const send = async () => {
    const token = localStorage.getItem("access");
    const headers = { "Content-Type": "application/json", ...options.headers };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  };
  let response = await send();
  if (response.status === 401) {
    const ok = await tryRefresh();
    if (ok) {
      response = await send();
    } else {
      redirectToLogin();
      throw new Error("სესია ვადაგასულია");
    }
  }
  if (!response.ok) {
    let data = {};
    try { data = await response.json(); } catch {}
    throw new Error(data.detail || data.error || `Error ${response.status}`);
  }
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) return response.json();
  return;
}

// ─── Contacts ─────────────────────────────────────────────────────
export const getContacts = (params = "") => request(`/api/mailer/contacts/${params ? "?" + params : ""}`);
export const createContact = (data) => request("/api/mailer/contacts/", { method: "POST", body: JSON.stringify(data) });
export const updateContact = (id, data) => request(`/api/mailer/contacts/${id}/`, { method: "PUT", body: JSON.stringify(data) });
export const deleteContact = (id) => request(`/api/mailer/contacts/${id}/`, { method: "DELETE" });
export const bulkDeleteContacts = (ids) => request("/api/mailer/contacts/bulk_delete/", { method: "POST", body: JSON.stringify({ ids }) });
export const getDuplicates = () => request("/api/mailer/contacts/duplicates/");

export async function importContacts(file, groupId) {
  const token = localStorage.getItem("access");
  const formData = new FormData();
  formData.append("file", file);
  if (groupId) formData.append("group", groupId);
  const response = await fetch(`${API_BASE_URL}/api/mailer/contacts/import_csv/`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || data.error || `Error ${response.status}`);
  }
  return response.json();
}

export async function exportContacts(params = "", filename = "kontaqtebi.xlsx") {
  const send = async () => {
    const token = localStorage.getItem("access");
    return fetch(`${API_BASE_URL}/api/mailer/contacts/export/${params ? "?" + params : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };
  let res = await send();
  if (res.status === 401) {
    const ok = await tryRefresh();
    if (ok) {
      res = await send();
    } else {
      redirectToLogin();
      throw new Error("სესია ვადაგასულია");
    }
  }
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || d.error || `Error ${res.status}`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ─── Groups ───────────────────────────────────────────────────────
export const getGroups = () => request("/api/mailer/contact-groups/");
export const getFlatGroups = () => request("/api/mailer/contact-groups/?flat=true");
export const createGroup = (data) => request("/api/mailer/contact-groups/", { method: "POST", body: JSON.stringify(data) });
export const updateGroup = (id, data) => request(`/api/mailer/contact-groups/${id}/`, { method: "PUT", body: JSON.stringify(data) });
export const deleteGroup = (id) => request(`/api/mailer/contact-groups/${id}/`, { method: "DELETE" });

// ─── Templates ────────────────────────────────────────────────────
export const getTemplates = () => request("/api/mailer/templates/");
export const createTemplate = (data) => request("/api/mailer/templates/", { method: "POST", body: JSON.stringify(data) });
export const updateTemplate = (id, data) => request(`/api/mailer/templates/${id}/`, { method: "PUT", body: JSON.stringify(data) });
export const deleteTemplate = (id) => request(`/api/mailer/templates/${id}/`, { method: "DELETE" });

// ─── SMTP Config ─────────────────────────────────────────────────
export const getSmtpConfigs = () => request("/api/mailer/smtp-config/");
export const createSmtpConfig = (data) => request("/api/mailer/smtp-config/", { method: "POST", body: JSON.stringify(data) });
export const updateSmtpConfig = (id, data) => request(`/api/mailer/smtp-config/${id}/`, { method: "PUT", body: JSON.stringify(data) });
export const deleteSmtpConfig = (id) => request(`/api/mailer/smtp-config/${id}/`, { method: "DELETE" });
export const testSmtpConfig = (data) => request("/api/mailer/smtp-config/test/", { method: "POST", body: JSON.stringify(data) });

// Multipart variant for SMTP config (used when uploading the stamp image)
export async function saveSmtpConfigMultipart(id, fields, stampFile) {
  const token = localStorage.getItem("access");
  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") formData.append(k, v);
  });
  if (stampFile) formData.append("stamp_image", stampFile);
  const url = id
    ? `${API_BASE_URL}/api/mailer/smtp-config/${id}/`
    : `${API_BASE_URL}/api/mailer/smtp-config/`;
  const res = await fetch(url, {
    method: id ? "PATCH" : "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || d.error || `Error ${res.status}`);
  }
  return res.json();
}

export async function deleteSmtpStamp(id) {
  // Send PATCH with empty stamp_image to clear it
  const token = localStorage.getItem("access");
  const formData = new FormData();
  formData.append("stamp_image", "");
  const res = await fetch(`${API_BASE_URL}/api/mailer/smtp-config/${id}/`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
}

// ─── Send ─────────────────────────────────────────────────────────
export const sendEmails = (data) => request("/api/mailer/send-logs/send/", { method: "POST", body: JSON.stringify(data) });
export const getSendLogs = () => request("/api/mailer/send-logs/");
export const getSendLog = (id) => request(`/api/mailer/send-logs/${id}/`);
export const resendLog = (id, failedOnly = false) => request(`/api/mailer/send-logs/${id}/resend/`, { method: "POST", body: JSON.stringify({ failed_only: failedOnly }) });
export const cancelLog = (id) => request(`/api/mailer/send-logs/${id}/cancel/`, { method: "POST" });

// ─── Attachments (PDF library) ───────────────────────────────────
export const getAttachments = () => request("/api/mailer/attachments/");
export const deleteAttachment = (id) => request(`/api/mailer/attachments/${id}/`, { method: "DELETE" });

export async function uploadAttachment(file, name = "") {
  const token = localStorage.getItem("access");
  const formData = new FormData();
  formData.append("file", file);
  if (name) formData.append("name", name);
  const res = await fetch(`${API_BASE_URL}/api/mailer/attachments/`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || d.error || `Error ${res.status}`);
  }
  return res.json();
}

// ─── Letterheads (header image library) ──────────────────────────
export const getLetterheads = () => request("/api/mailer/letterheads/");
export const deleteLetterhead = (id) => request(`/api/mailer/letterheads/${id}/`, { method: "DELETE" });

export async function uploadLetterhead(file, name = "") {
  const token = localStorage.getItem("access");
  const formData = new FormData();
  formData.append("image", file);
  if (name) formData.append("name", name);
  const res = await fetch(`${API_BASE_URL}/api/mailer/letterheads/`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || d.error || `Error ${res.status}`);
  }
  return res.json();
}
